import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  ToolOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import AppCard from '../../../components/cards/AppCard';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import {
  createRowActionConfig,
  deleteRowActionConfig,
  getActionConfigModules,
  getCustomForms,
  getMenuActions,
  getMenuModules,
  getRowActionConfigs,
  updateRowActionConfig,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import '../../../styles/FormGroupsSection.css';
import { getRuntimeEmailTemplates } from '../../../services/emailTemplateApi';

// OTP-gated config actions surfaced as buttons next to "Add Action". Each opens
// the OtpGateModal which mails a code and performs the action on verification.
// These operate on ALL modules'/roles' row-action configs at once (not just the
// currently selected module), same as the Form Groups / Detail Groups pattern.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all row actions',   confirmLabel: 'Reset All',       danger: true,
    description: 'This clears every configured row action for every module in this workspace. Actions have no built-in defaults, so this leaves every module with no actions until reconfigured.' },
  save:    { action: 'save',    title: 'Save current as default', confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current row actions for all modules and roles as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',   confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current row actions for all modules and roles with the last saved default.' },
};

const { Text } = Typography;

const ICON_OPTIONS = [
  { value: 'delete',      label: 'delete (trash)' },
  { value: 'edit',        label: 'edit (pencil)' },
  { value: 'mail',        label: 'mail (envelope)' },
  { value: 'shareAlt',    label: 'shareAlt (share)' },
  { value: 'swap',        label: 'swap (arrows)' },
  { value: 'thunderbolt', label: 'thunderbolt (lightning)' },
  { value: 'userAdd',     label: 'userAdd (add user)' },
  { value: 'userSwitch',  label: 'userSwitch (switch user)' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'navigate', label: 'Navigate (open page)' },
  { value: 'modal',    label: 'Modal (open popup)' },
  { value: 'api',      label: 'API (call endpoint)' },
  { value: 'copy',     label: 'Copy (clipboard)' },
  { value: 'document', label: 'Document (open viewer)' },
  { value: 'group',    label: 'Group (submenu of child actions)' },
];

const API_METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE'];
const BUILT_IN_POPUPS = [
  'quick-submit', 'add-notes', 'mail', 'change-status', 'tag-to-job',
  'add-to-groups', 'self-assign', 'others-assign', 'delete', 'vendor-share',
  'sticky-notes', 'move-to-onboarding', 'approve-rate', 'reject',
  'add-batch-candidate',
];

const EMPTY_ACTION = {
  key: '', label: '', icon: '', actionType: 'navigate',
  routeTemplate: '', linkTarget: '_self', apiMethod: 'POST', apiPath: '',
  popupKey: '', popupStateText: '', copyField: '', documentField: '', allowedTabs: [],
  permission: '', permissionModule: '', permissionAction: '', permissionKey: '', allowMultiple: false, danger: false, showInList: true,
  showInDetail: false, detailPlacement: 'more',
  // Done state (config-driven success terminus). doneField/doneOperator/doneValue
  // are the split form of the persisted `doneWhen` condition, recombined on save.
  doneLabel: '', doneField: '', doneOperator: 'notEmpty', doneValue: '', doneTone: 'success',
};

// Operators offered for the done-state condition — a practical subset of the
// action-rule operators (the ones that make sense for a single lifecycle flag).
const DONE_OPERATOR_OPTIONS = [
  { value: 'notEmpty', label: 'is set (not empty)' },
  { value: 'eq',       label: 'equals' },
  { value: 'ne',       label: 'not equals' },
  { value: 'isEmpty',  label: 'is empty' },
];

function newKey() {
  return `action_${Date.now()}`;
}

/* ── Action editor ─────────────────────────────────────────────────────────── */
function ActionModal({ open, initial, isChild, popupForms, templateOptions, permissionModules, permissionActions, onClose, onSave }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const actionType = Form.useWatch('actionType', form) ?? 'navigate';
  const showInDetail = Form.useWatch('showInDetail', form) ?? false;
  const popupKey = Form.useWatch('popupKey', form) ?? '';
  const doneOperator = Form.useWatch('doneOperator', form) ?? 'notEmpty';

  useEffect(() => {
    if (open) {
      // Existing "permission" is a flat "module.action" string (e.g. "job.edit") —
      // split it into the two dropdowns below; recombined on save.
      const [permModule = '', permAction = ''] = (initial?.permission ?? '').split('.');
      form.setFieldsValue({
        ...EMPTY_ACTION,
        ...initial,
        permissionModule: permModule,
        permissionAction: permAction,
        popupStateText: initial?.popupState
          ? JSON.stringify(initial.popupState, null, 2)
          : '',
        // Split the persisted doneWhen condition back into its edit fields.
        doneField: initial?.doneWhen?.field ?? '',
        doneOperator: initial?.doneWhen?.operator ?? 'notEmpty',
        doneValue: initial?.doneWhen?.value ?? '',
        doneLabel: initial?.doneLabel ?? '',
        doneTone: initial?.doneTone ?? 'success',
      });
    }
  }, [open, initial, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    let popupState;
    if (values.popupStateText?.trim()) {
      try {
        popupState = JSON.parse(values.popupStateText);
      } catch {
        form.setFields([{ name: 'popupStateText', errors: ['Enter valid JSON'] }]);
        return;
      }
    }
    const action = { ...values };
    delete action.popupStateText;
    const { permissionModule, permissionAction } = action;
    delete action.permissionModule;
    delete action.permissionAction;
    action.permission = permissionModule && permissionAction ? `${permissionModule}.${permissionAction}` : '';

    // Recombine the done-state edit fields into a single `doneWhen` condition.
    // A blank field (or an isEmpty/notEmpty operator) drops the value; a blank
    // field entirely clears the done state.
    const { doneField, doneOperator, doneValue } = action;
    delete action.doneField;
    delete action.doneOperator;
    delete action.doneValue;
    if (doneField?.trim()) {
      const operator = doneOperator || 'notEmpty';
      const needsValue = operator === 'eq' || operator === 'ne';
      action.doneWhen = {
        field: doneField.trim(),
        operator,
        ...(needsValue ? { value: (doneValue ?? '').trim() } : {}),
      };
      if (!action.doneLabel?.trim()) action.doneLabel = '';
    } else {
      action.doneWhen = undefined;
      action.doneLabel = '';
    }
    if (action.actionType !== 'modal' || !['mail', 'vendor-share'].includes(action.popupKey)) {
      delete action.defaultTemplateId;
    }
    setSaving(true);
    try {
      await onSave({ ...action, ...(popupState ? { popupState } : { popupState: undefined }) });
    } finally {
      setSaving(false);
    }
  };

  const popupOptions = [
    ...BUILT_IN_POPUPS.map((key) => ({ value: key, label: `${key} (built-in)` })),
    ...popupForms.map((popup) => ({
      value: popup.action,
      label: `${popup.label} (${popup.action})`,
    })),
  ].filter((option, index, options) => options.findIndex((item) => item.value === option.value) === index);

  return (
    <Modal
      title={`${initial?.key ? 'Edit' : 'Add'}${isChild ? ' Child' : ''} Action`}
      width={720}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Save Action"
      okButtonProps={{ icon: <SaveOutlined /> }}
      className="fg-editor-modal action-editor-modal"
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_ACTION} className="action-editor-form">
        <div className="action-editor-intro">
          Choose what the action does, then decide where it appears and who can use it.
        </div>
        <div className="action-editor-grid">
        <Form.Item name="key" label="Key" rules={[{ required: true, message: 'Key is required' }]}>
          <Input placeholder="e.g. sourceCandidates" />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
          <Input placeholder="e.g. Source Candidates" />
        </Form.Item>
        <Form.Item name="icon" label="Icon" rules={[{ required: false, message: 'Icon is required' }]}>
          <Select options={ICON_OPTIONS} placeholder="Select icon" allowClear/>
        </Form.Item>
        <Form.Item name="actionType" label="Action Type" rules={[{ required: true }]}>
          <Select
            options={ACTION_TYPE_OPTIONS}
          />
        </Form.Item>
        </div>

        {actionType === 'group' && (
          <Alert
            type="info"
            showIcon
            message="Group actions don't run anything themselves."
            description='This action renders as a submenu. Save it, then use "Add Child Action" on its row to configure what appears inside (e.g. Tag To Job, Send Mail, Add Notes).'
            style={{ marginBottom: 16 }}
          />
        )}

        {actionType !== 'group' && <div className="action-editor-section-title">Behaviour</div>}

        {actionType === 'navigate' && (
          <>
            <Form.Item name="routeTemplate" label="Route Template" rules={[{ required: true }]}>
              <Input placeholder="e.g. /app/jobs/edit/:_id" />
            </Form.Item>
            <Form.Item name="linkTarget" label="Open in" tooltip="New tab is useful for a reference page the user shouldn't lose their place to (e.g. Open JD from Quick Submit).">
              <Select options={[
                { value: '_self', label: 'Same tab' },
                { value: '_blank', label: 'New tab' },
              ]} />
            </Form.Item>
          </>
        )}

        {actionType === 'modal' && (
          <>
            <Form.Item name="popupKey" label="Popup" rules={[{ required: true, message: 'Select a popup' }]}
              tooltip="Admin popup forms and built-in popup integrations are listed together.">
              <Select showSearch optionFilterProp="label" options={popupOptions} placeholder="Select popup form" />
            </Form.Item>
            <Form.Item name="popupStateText" label="Popup Context (JSON, optional)"
              tooltip='Static context passed to the popup, for example {"notesfor":"candidate"}'>
              <Input.TextArea rows={4} placeholder={'{"notesfor":"candidate"}'} />
            </Form.Item>
            <Space.Compact block>
              <Form.Item name="apiMethod" label="Submit Method" style={{ width: 150 }}>
                <Select options={API_METHOD_OPTIONS.map((m) => ({ value: m, label: m }))} />
              </Form.Item>
              <Form.Item name="apiPath" label="Submit API (optional)" style={{ flex: 1 }}
                tooltip="Leave empty to use the built-in handler for the selected popup key.">
                <Input placeholder="e.g. /module-data-update" />
              </Form.Item>
            </Space.Compact>
          </>
        )}

        {actionType === 'modal' && ['mail', 'vendor-share'].includes(popupKey) && (
          <Form.Item
            name="defaultTemplateId"
            label="Default Email Template"
            tooltip="This template is selected and loaded automatically whenever this action opens the mail composer."
          >
            <Select allowClear showSearch optionFilterProp="label" options={templateOptions} placeholder="No default — user selects in composer" />
          </Form.Item>
        )}

        {actionType === 'api' && (
          <>
            <Form.Item name="apiMethod" label="API Method">
              <Select options={API_METHOD_OPTIONS.map((m) => ({ value: m, label: m }))} />
            </Form.Item>
            <Form.Item name="apiPath" label="API Path">
              <Input placeholder="e.g. /jobs/:_id" />
            </Form.Item>
          </>
        )}

        {actionType === 'copy' && (
          <Form.Item name="copyField" label="Record Field to Copy" rules={[{ required: true }]}>
            <Input placeholder="e.g. email or applicantRefrenceId" />
          </Form.Item>
        )}

        {actionType === 'document' && (
          <Form.Item name="documentField" label="Document Field" rules={[{ required: true }]}>
            <Input placeholder="e.g. resume or attachment" />
          </Form.Item>
        )}

        <div className="action-editor-section-title">Availability and access</div>
        <Form.Item name="allowedTabs" label="Allowed Tabs (optional)"
          tooltip="Leave empty to show on every tab.">
          <Select mode="tags" tokenSeparators={[',']} placeholder="e.g. allJobs" />
        </Form.Item>

        <Space.Compact block>
          <Form.Item name="permissionModule" label="Role Permission — Module (optional)" style={{ width: '50%' }}
            tooltip="Which module's RBAC permission gates this action. Leave both empty to show for every permitted role.">
            <Select allowClear showSearch optionFilterProp="label" placeholder="e.g. job"
              options={permissionModules.map((m) => ({ value: m, label: m }))} />
          </Form.Item>
          <Form.Item name="permissionAction" label="Role Permission — Action" style={{ width: '50%' }}>
            <Select allowClear showSearch optionFilterProp="label" placeholder="e.g. edit"
              options={permissionActions.map((a) => ({ value: a, label: a }))} />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="permissionKey" label="Permission Key (row-level gate)" tooltip="Maps to record.actionPermission.<key>. Leave blank to always show.">
          <Input placeholder="e.g. edit, sourceCandidates, sharePermission" />
        </Form.Item>

        <div className="action-toggle-grid">
          <Form.Item name="allowMultiple" label="Multiple Records" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="danger" label="Danger" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInList" label="Show in List" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInDetail" label="Show in Detail" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
        {showInDetail && (
          <Form.Item name="detailPlacement" label="Detail Placement">
            <Select options={[
              { value: 'primary', label: 'Primary button' },
              { value: 'more', label: 'More actions menu' },
            ]} />
          </Form.Item>
        )}

        <div className="action-editor-section-title">Done state (optional)</div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Turn this action into a disabled success label once a condition is met."
          description='Example: when the record field "onboardingStatus" is set, "Move to Onboarding" shows as a disabled "Moved to Onboarded". Leave the field blank to disable.'
        />
        <Form.Item name="doneLabel" label="Done Label"
          tooltip="Text shown once the done condition is met (e.g. Moved to Onboarded). Falls back to the normal label if blank.">
          <Input placeholder="e.g. Moved to Onboarded" />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="doneField" label="When Record Field" style={{ width: '40%' }}
            tooltip="A field on the record to test (dotted paths allowed, e.g. onboardingStatus).">
            <Input placeholder="e.g. onboardingStatus" />
          </Form.Item>
          <Form.Item name="doneOperator" label="Condition" style={{ width: '30%' }}>
            <Select options={DONE_OPERATOR_OPTIONS} />
          </Form.Item>
          <Form.Item name="doneValue" label="Value" style={{ width: '30%' }}
            hidden={doneOperator !== 'eq' && doneOperator !== 'ne'}>
            <Input placeholder="e.g. In Progress" />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="doneTone" label="Done Colour">
          <Select options={[
            { value: 'success', label: 'Success (green)' },
            { value: 'primary', label: 'Primary (blue)' },
            { value: 'danger', label: 'Danger (red)' },
          ]} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ── Main Section ──────────────────────────────────────────────────────────── */
export default function RowActionsSection() {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  // This screen edits the DEFAULT (All Roles) action library only. Per-role
  // overrides live in Admin → User Types → Config → Actions (RoleActionsEditor).
  const selectedRoleId = 0;

  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [popupForms, setPopupForms] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [gate, setGate] = useState(null); // { action, title, confirmLabel, danger, description }
  const [query, setQuery] = useState('');
  const [permissionModules, setPermissionModules] = useState([]);
  const [permissionActions, setPermissionActions] = useState([]);

  /* Load modules once — configured modules plus every DB collection, so
     actions can be configured for any module dynamically. */
  useEffect(() => {
    getActionConfigModules().then((mods) => {
      const unique = normalizeModuleOptions(mods).map((m) => m.value);
      setModules(unique);
      setSelectedModule((current) => current || unique[0] || '');
    }).catch(() => {
      setModules([]);
    });
  }, []);

  /* Load the RBAC permission catalog (module/action master data) once, for
     the Permission dropdowns in the action editor — the same catalog Roles'
     Permissions tab reads from. */
  useEffect(() => {
    Promise.all([getMenuModules().catch(() => []), getMenuActions().catch(() => [])])
      .then(([mods, actions]) => {
        setPermissionModules([...new Set(mods.map((m) => m.MENUNAME ?? m.menuName).filter(Boolean))]);
        setPermissionActions([...new Set(actions.map((a) => a.permissionKey ?? a.PERMISSIONKEY).filter(Boolean))]);
      });
  }, []);

  /* Load configs when module changes */
  const loadConfigs = useCallback(async () => {
    if (!selectedModule) return;
    setLoading(true);
    setError(null);
    setConfigs([]);
    try {
      const data = await getRowActionConfigs(selectedModule);
      setConfigs(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => { queueMicrotask(() => { loadConfigs(); }); }, [loadConfigs]);

  useEffect(() => {
    Promise.all([
      getCustomForms(selectedModule, 'popup').catch(() => []),
      getRuntimeEmailTemplates({ module: selectedModule }).catch(() => []),
    ]).then(([forms, templates]) => {
      setPopupForms(forms);
      setTemplateOptions(templates.map((template) => ({ value: template._id ?? template.id, label: `${template.name} · ${template.action}` })));
    });
  }, [selectedModule]);

  /* Pick the default (roleId 0) config from already-loaded configs. The
     backend list is alias-tolerant (job/jobs/Job), so prefer the config whose
     module key matches the selection exactly, then fall back to any alias
     variant — otherwise editing could target a legacy duplicate. */
  useEffect(() => {
    queueMicrotask(() => {
      const forRole = configs.filter((c) => c.roleId === selectedRoleId);
      const match = forRole.find((c) => c.module === selectedModule) ?? forRole[0];
      setActiveConfig(match ?? null);
      setActions(match ? (match.rowActions ?? []) : []);
      setDirty(false);
    });
  }, [selectedModule, configs]);

  /* Save */
  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeConfig) {
        await updateRowActionConfig(activeConfig.id ?? activeConfig._id, actions);
        setConfigs((prev) => prev.map((config) => (
          (config.id ?? config._id) === (activeConfig.id ?? activeConfig._id)
            ? { ...config, rowActions: actions }
            : config
        )));
        message.success('Config saved');
      } else {
        const created = await createRowActionConfig({ module: selectedModule, roleId: selectedRoleId, rowActions: actions });
        const newDoc = created?.data ?? created;
        setActiveConfig(newDoc);
        setConfigs((prev) => [...prev, newDoc]);
        message.success('Config created');
      }
      setDirty(false);
    } catch (e) {
      message.error(e.message);
      if (String(e.message ?? '').toLowerCase().includes('already exists')) loadConfigs();
    } finally {
      setSaving(false);
    }
  };

  /* Delete config */
  const handleDeleteConfig = async () => {
    if (!activeConfig) return;
    try {
      await deleteRowActionConfig(activeConfig.id ?? activeConfig._id);
      message.success('Config deleted');
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
    }
  };

  /* Action row operations. editingParentKey is set when adding/editing a
     child of a "group" action (e.g. adding "Tag To Job" under "Shortlist"),
     null for top-level actions. */
  const [editingParentKey, setEditingParentKey] = useState(null);

  const openAddDrawer = (parentKey = null) => {
    // Guard against `onClick={openAddDrawer}` handing this the click's
    // SyntheticEvent instead of a real key (as happened for "New action" —
    // a truthy non-string parentKey silently routed saves into the "add
    // child to a matching group" branch, which matched nothing and dropped
    // the new action). Only a string key is ever a valid parent.
    setEditingParentKey(typeof parentKey === 'string' ? parentKey : null);
    setEditingAction(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (action, parentKey = null) => {
    setEditingParentKey(parentKey);
    setEditingAction(action);
    setDrawerOpen(true);
  };

  const handleSaveAction = async (values) => {
    let nextActions;
    if (editingParentKey) {
      nextActions = actions.map((parent) => {
        if (parent.key !== editingParentKey) return parent;
        const children = parent.children ?? [];
        const nextChildren = editingAction
          ? children.map((c) => (c.key === editingAction.key ? { ...c, ...values } : c))
          : [...children, { ...EMPTY_ACTION, ...values, _localKey: newKey() }];
        return { ...parent, children: nextChildren };
      });
    } else if (editingAction) {
      nextActions = actions.map((a) => (a.key === editingAction.key ? { ...a, ...values } : a));
    } else {
      nextActions = [...actions, { ...EMPTY_ACTION, ...values, _localKey: newKey() }];
    }

    try {
      if (activeConfig) {
        await updateRowActionConfig(activeConfig.id ?? activeConfig._id, nextActions);
        setConfigs((prev) => prev.map((config) => (
          (config.id ?? config._id) === (activeConfig.id ?? activeConfig._id)
            ? { ...config, rowActions: nextActions }
            : config
        )));
        message.success('Config saved');
      } else {
        const created = await createRowActionConfig({ module: selectedModule, roleId: selectedRoleId, rowActions: nextActions });
        const newDoc = created?.data ?? created;
        setActiveConfig(newDoc);
        setConfigs((prev) => [...prev, newDoc]);
        message.success('Config created');
      }
      setActions(nextActions);
      setDirty(false);
      setDrawerOpen(false);
    } catch (e) {
      message.error(e.message);
      // 409 "config already exists" means our list is stale (e.g. the config
      // was created under an alias key or in another tab) — resync so the next
      // save takes the update path instead of failing forever.
      if (String(e.message ?? '').toLowerCase().includes('already exists')) loadConfigs();
    }
  };

  const handleDeleteAction = (actionKey, parentKey = null) => {
    if (parentKey) {
      setActions((prev) => prev.map((parent) => (
        parent.key === parentKey
          ? { ...parent, children: (parent.children ?? []).filter((c) => c.key !== actionKey) }
          : parent
      )));
    } else {
      setActions((prev) => prev.filter((a) => a.key !== actionKey));
    }
    setDirty(true);
  };

  const moveAction = (index, dir) => {
    setActions((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  /* OTP-gated Reset All / Save Default / Restore Default */
  function openGate(action) { setGate(CONFIG_GATE_META[action]); }
  function handleGateVerified() {
    setGate(null);
    loadConfigs();
  }

  const visibleActions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return actions;
    return actions.filter((action) => [
      action.label, action.key, action.actionType, action.popupKey,
      action.routeTemplate, action.apiPath, action.permissionKey,
    ].some((value) => String(value ?? '').toLowerCase().includes(needle)));
  }, [actions, query]);

  const columns = [
    { title: 'Order', key: 'order', width: 76,
      render: (_, action) => (
        <Space>
          <Button size="small" type="text" disabled={action._actionIndex === 0} onClick={() => moveAction(action._actionIndex, -1)}>↑</Button>
          <Button size="small" type="text" disabled={action._actionIndex === actions.length - 1} onClick={() => moveAction(action._actionIndex, 1)}>↓</Button>
        </Space>
      ),
    },
    { title: 'Action', key: 'identity', width: 210, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text strong>{action.label || action.key}</Text>
        <Text type="secondary" code>{action.key}</Text>
      </div>
    ) },
    { title: 'Behaviour', key: 'destination', render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Tag color="blue">{action.actionType}</Tag>
        <Text type="secondary" ellipsis={{ tooltip: true }}>
          {action.actionType === 'group'
            ? `${(action.children ?? []).length} child action${(action.children ?? []).length === 1 ? '' : 's'}`
            : (action.popupKey || action.routeTemplate || action.apiPath || action.copyField || action.documentField || 'No destination')}
        </Text>
      </div>
    ),
    },
    { title: 'Visible in', key: 'visibility', width: 210, render: (_, action) => (
      <Space size={[4, 4]} wrap>
        {action.showInList && <Tag color="green">List</Tag>}
        {(action.showInDetail ?? action.showInList) && <Tag color="purple">Detail · {action.detailPlacement || 'more'}</Tag>}
        {action.allowMultiple && <Tag>Multi-select</Tag>}
        {action.danger && <Tag color="red">Destructive</Tag>}
        {!action.showInList && !(action.showInDetail ?? action.showInList) && <Text type="secondary">Nowhere</Text>}
      </Space>
    ) },
    { title: 'Access', key: 'access', width: 170, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text type="secondary">{action.permission || 'All permitted roles'}</Text>
        {action.permissionKey && <Text type="secondary" code>{action.permissionKey}</Text>}
      </div>
    ),
    },
    { title: '', key: 'actions', width: 110, fixed: 'right',
      render: (_, record) => (
        <Space>
          {record.actionType === 'group' && (
            <Tooltip title="Add child action">
              <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => openAddDrawer(record.key)} />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
          </Tooltip>
          <Popconfirm title="Remove this action?" onConfirm={() => handleDeleteAction(record.key)} okText="Remove" okType="danger">
            <Tooltip title="Remove">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const childColumns = (parentKey) => [
    { title: 'Action', key: 'identity', width: 210, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text strong>{action.label || action.key}</Text>
        <Text type="secondary" code>{action.key}</Text>
      </div>
    ) },
    { title: 'Behaviour', key: 'destination', render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Tag color="blue">{action.actionType}</Tag>
        <Text type="secondary" ellipsis={{ tooltip: true }}>
          {action.popupKey || action.routeTemplate || action.apiPath || action.copyField || action.documentField || 'No destination'}
        </Text>
      </div>
    ) },
    { title: 'Access', key: 'access', width: 170, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text type="secondary">{action.permission || 'All permitted roles'}</Text>
        {action.permissionKey && <Text type="secondary" code>{action.permissionKey}</Text>}
      </div>
    ) },
    { title: '', key: 'actions', width: 80, fixed: 'right', render: (_, record) => (
      <Space>
        <Tooltip title="Edit">
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditDrawer(record, parentKey)} />
        </Tooltip>
        <Popconfirm title="Remove this child action?" onConfirm={() => handleDeleteAction(record.key, parentKey)} okText="Remove" okType="danger">
          <Tooltip title="Remove">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div className="fg-section admin-config-section">

      {/* Header card */}
      <AppCard
        icon={<ToolOutlined />}
        title="Actions"
        extra={
          <Dropdown trigger={['click']} menu={{ items: [
            { key: 'save', label: 'Save current as default', icon: <SaveOutlined />, onClick: () => openGate('save') },
            { key: 'restore', label: 'Restore saved default', icon: <CloudDownloadOutlined />, onClick: () => openGate('restore') },
            { type: 'divider' },
            { key: 'reset', label: 'Reset all actions', icon: <ReloadOutlined />, danger: true, onClick: () => openGate('reset') },
          ] }}>
            <Button size="small" icon={<MoreOutlined />}>Workspace defaults</Button>
          </Dropdown>
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">
          Build one action once, then control its List and Detail placement, permissions, tabs, and multi-select behaviour.
        </Text>
      </AppCard>

      {/* Selector + controls */}
      <AppCard styles={{ body: { padding: '14px 18px' } }}>
        <div className="admin-config-context">
          <div className="admin-config-context-copy">
            <Text strong>Configuration context</Text>
            <Text type="secondary">Choose a module. This is the default action library for all roles — per-role overrides are set in User Types → Config → Actions.</Text>
          </div>
          <div className="admin-config-context-controls">
          <label className="admin-config-control">
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Module</Text>
            <Select
              showSearch optionFilterProp="label" style={{ width: 220 }}
              mode="tags" maxCount={1}
              placeholder="Pick or type a module key"
              value={selectedModule ? [selectedModule] : []}
              onChange={(v) => setSelectedModule(String(v[v.length - 1] ?? '').trim())}
              options={modules.map((m) => ({ value: m, label: m }))}
            />
          </label>
          <div className="admin-config-reload">
            <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
              Reload
            </Button>
          </div></div>
        </div>
      </AppCard>

      {/* Error */}
      {error && <Alert type="error" message={error} showIcon />}

      {/* Actions table */}
      <AppCard
        icon={<ToolOutlined />}
        title={
          <Space wrap>
            <span>{actions.length} configured action{actions.length === 1 ? '' : 's'}</span>
            {activeConfig
              ? <Tag color="green">Default config loaded (All Roles)</Tag>
              : <Tag color="orange">No config — will create on Save</Tag>
            }
          </Space>
        }
        extra={
          <Space wrap>
            {activeConfig && (
              <Popconfirm title="Delete this entire config?" onConfirm={handleDeleteConfig} okText="Delete" okType="danger">
                <Button danger size="small" icon={<DeleteOutlined />}>Delete Config</Button>
              </Popconfirm>
            )}
            <Button icon={<PlusOutlined />} onClick={() => openAddDrawer()}>New action</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty}
              onClick={handleSave}
            >
              Save Config
            </Button>
          </Space>
        }
      >
        <div className="admin-config-table-tools">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search actions"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {dirty && <Text type="warning">Unsaved changes</Text>}
        </div>
        <Spin spinning={loading}>
          <Table
            dataSource={visibleActions.map((a, i) => ({
              ...a,
              _actionIndex: actions.indexOf(a),
              _rowKey: a.key || a._localKey || i,
            }))}
            rowKey="_rowKey"
            columns={columns}
            pagination={false}
            size="small"
            className="fg-table admin-config-table"
            scroll={{ x: 820 }}
            locale={{ emptyText: 'No actions configured. Click "Add Action" to start.' }}
            expandable={{
              rowExpandable: (record) => record.actionType === 'group',
              expandedRowRender: (record) => (
                <Table
                  dataSource={(record.children ?? []).map((c, i) => ({ ...c, _rowKey: c.key || c._localKey || i }))}
                  rowKey="_rowKey"
                  columns={childColumns(record.key)}
                  pagination={false}
                  size="small"
                  className="fg-table admin-config-table"
                  locale={{ emptyText: 'No child actions yet. Use the + button on this row to add one (e.g. Tag To Job, Send Mail, Add Notes).' }}
                />
              ),
            }}
          />
        </Spin>
      </AppCard>

      <ActionModal
        open={drawerOpen}
        initial={editingAction}
        isChild={!!editingParentKey}
        popupForms={popupForms}
        templateOptions={templateOptions}
        permissionModules={permissionModules}
        permissionActions={permissionActions}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveAction}
      />

      <OtpGateModal
        open={!!gate}
        scope="rowActions"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
